import { isGenericRulePattern, normalizeRulePattern, parseDescription } from '@budgie/shared';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type PatternSelectorTxn = {
  id: string;
  description: string;
  isDuplicate: boolean;
};

type SegmentKey = 'prefix' | 'date' | 'merchant';

interface PatternSelectorProps {
  description: string;
  transactions: PatternSelectorTxn[];
  initialPattern?: string | null | undefined;
  onCancel: () => void;
  onConfirm: (pattern: string) => void;
}

export function PatternSelector({
  description,
  transactions,
  initialPattern,
  onCancel,
  onConfirm,
}: PatternSelectorProps) {
  const parsed = useMemo(() => parseDescription(description), [description]);
  const segments = useMemo(
    () =>
      [
        {
          key: 'prefix' as const,
          label: 'Prefix',
          value: parsed.prefix,
        },
        { key: 'date' as const, label: 'Datum', value: parsed.date },
        {
          key: 'merchant' as const,
          label: 'Butik/Mottagare',
          value: parsed.merchant,
        },
      ].filter((segment) => Boolean(segment.value)),
    [parsed],
  );

  const [manualMode, setManualMode] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState<Set<SegmentKey>>(new Set(['merchant']));
  const [manualPattern, setManualPattern] = useState(initialPattern ?? parsed.merchant);

  useEffect(() => {
    const nextSelection = new Set<SegmentKey>();
    if (!initialPattern) {
      nextSelection.add('merchant');
    } else {
      const normalizedInitial = normalizeRulePattern(initialPattern);
      for (const segment of segments) {
        if (segment.value && normalizedInitial.includes(normalizeRulePattern(segment.value))) {
          nextSelection.add(segment.key);
        }
      }
      if (nextSelection.size === 0) {
        nextSelection.add('merchant');
      }
    }

    setManualMode(false);
    setSelectedSegments(nextSelection);
    setManualPattern(initialPattern ?? parsed.merchant);
  }, [initialPattern, parsed.merchant, segments]);

  const selectedPattern = manualMode
    ? manualPattern
    : segments
        .filter((segment) => selectedSegments.has(segment.key))
        .map((segment) => segment.value)
        .join(' ')
        .trim();

  const normalizedPattern = normalizeRulePattern(selectedPattern);
  const matchCount = normalizedPattern
    ? transactions.filter(
        (transaction) =>
          !transaction.isDuplicate &&
          normalizeRulePattern(transaction.description).includes(normalizedPattern),
      ).length
    : 0;
  const isTooShort = selectedPattern.trim().length > 0 && selectedPattern.trim().length < 3;
  const genericWarning = normalizedPattern && isGenericRulePattern(selectedPattern);

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-2xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Välj regelmönster</DialogTitle>
          <DialogDescription className="mt-0.5 text-xs text-muted-foreground">
            Välj den del av texten som ska matchas i framtida importer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">Transaktion</p>
            <div className="rounded-xl bg-muted px-3 py-2 font-mono text-sm break-all text-foreground">
              {description}
            </div>
          </div>

          {!manualMode && segments.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium text-muted-foreground">Föreslagna delar</p>
              <div className="flex flex-wrap gap-2">
                {segments.map((segment) => {
                  const isSelected = selectedSegments.has(segment.key);
                  return (
                    <Button
                      key={segment.key}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => {
                        setSelectedSegments((prev) => {
                          const next = new Set(prev);
                          if (next.has(segment.key)) {
                            next.delete(segment.key);
                          } else {
                            next.add(segment.key);
                          }
                          return next;
                        });
                      }}
                      variant={isSelected ? 'secondary' : 'outline'}
                      size="sm"
                      className={`h-auto rounded-full px-3 py-1.5 ${isSelected ? 'border-primary bg-primary/10 text-primary hover:bg-primary/15' : 'text-muted-foreground hover:bg-muted'}`}
                    >
                      <span className="mr-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                        {segment.label}
                      </span>
                      <span className="font-mono">{segment.value}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {manualMode && (
            <div>
              <Label htmlFor="manual-rule-pattern" className="text-xs text-muted-foreground">
                Manuell regeltext
              </Label>
              <Input
                id="manual-rule-pattern"
                value={manualPattern}
                onChange={(event) => setManualPattern(event.target.value)}
                className="mt-1 h-10 font-mono"
              />
            </div>
          )}

          <Button
            type="button"
            onClick={() => setManualMode((current) => !current)}
            variant="link"
            className="h-auto w-fit px-0"
          >
            {manualMode ? 'Använd föreslagna delar' : 'Redigera manuellt'}
          </Button>

          <div className="space-y-2 rounded-xl border border-border px-3 py-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Förhandsvisning</p>
              <p className="mt-1 font-mono text-sm text-foreground">
                {selectedPattern || 'Ingen text vald'}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              Matchar {matchCount} transaktion
              {matchCount === 1 ? '' : 'er'} i den här importen.
            </p>
            {isTooShort && (
              <p className="text-xs text-negative">Välj minst 3 tecken för att skapa en regel.</p>
            )}
            {genericWarning && (
              <p className="text-xs text-warning">
                Varning: mönstret är väldigt generiskt och kan matcha flera olika transaktioner.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Avbryt
          </Button>
          <Button
            type="button"
            onClick={() => onConfirm(selectedPattern.trim())}
            disabled={!selectedPattern.trim() || isTooShort}
          >
            Använd mönster
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
